"use client";

import { useMemo, useCallback } from "react";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { NewsPageQueryResult } from "@/sanity.types";
import slugify from "slugify";

type UseNewsFilterProps = {
	news: NonNullable<NewsPageQueryResult>["orderOfNewsList"];
	categories: NonNullable<NewsPageQueryResult>["newsCategories"];
};

const ALL_NEWS_SLUG = "all";

export function useNewsFilter({ news, categories }: UseNewsFilterProps) {
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const router = useRouter();

	// Get category from URL - this is our single source of truth
	const activeCategorySlug = searchParams.get("category") || ALL_NEWS_SLUG;

	// Get only categories that are actually used in projects
	const availableCategories = useMemo(() => {
		const usedCategoryIds = new Set<string>();

		// Collect all category IDs that are used in projects
		news.forEach((news) => {
			if (news.category?._id) {
				usedCategoryIds.add(news.category._id);
			}
		});

		// Filter categories to only include those that are used
		return categories.filter((category) => usedCategoryIds.has(category._id));
	}, [news, categories]);

	// Create category slug mapping (using available categories only)
	const categorySlugMap = useMemo(() => {
		const map = new Map<string, string>();
		availableCategories.forEach((category) => {
			const slug = slugify(category.categoryName, { lower: true, strict: true });
			map.set(category._id, slug);
		});
		return map;
	}, [availableCategories]);

	// Get active category ID from slug
	const activeCategoryId = useMemo(() => {
		if (activeCategorySlug === ALL_NEWS_SLUG) return null;

		for (const [categoryId, slug] of categorySlugMap.entries()) {
			if (slug === activeCategorySlug) return categoryId;
		}
		return null;
	}, [activeCategorySlug, categorySlugMap]);

	// Filter projects based on active category
	const filteredNews = useMemo(() => {
		if (activeCategorySlug === ALL_NEWS_SLUG) {
			return news;
		}

		return news.filter((news) => {
			if (!news.category) return false;
			const newsCategorySlug = categorySlugMap.get(news.category._id);
			return newsCategorySlug === activeCategorySlug;
		});
	}, [news, activeCategorySlug, categorySlugMap]);

	// Memoized category counts for better performance
	const categoryNewsCountMap = useMemo(() => {
		const countMap = new Map<string, number>();
		news.forEach((news) => {
			if (news.category?._id) {
				const currentCount = countMap.get(news.category._id) || 0;
				countMap.set(news.category._id, currentCount + 1);
			}
		});
		return countMap;
	}, [news]);

	// Update URL when category changes
	const updateUrl = useCallback(
		(categorySlug: string) => {
			const params = new URLSearchParams(searchParams.toString());

			if (categorySlug === ALL_NEWS_SLUG) {
				params.delete("category");
			} else {
				params.set("category", categorySlug);
			}

			const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname;
			router.replace(newUrl, { scroll: false });
		},
		[searchParams, pathname, router],
	);

	// Handle category change
	const handleCategoryChange = useCallback(
		(categoryId: string | null) => {
			let newSlug = ALL_NEWS_SLUG;

			if (categoryId) {
				newSlug = categorySlugMap.get(categoryId) || ALL_NEWS_SLUG;
			}

			updateUrl(newSlug);
		},
		[categorySlugMap, updateUrl],
	);

	// Optimized count function using memoized map
	const getNewsCountForCategory = useCallback(
		(categoryId: string) => {
			return categoryNewsCountMap.get(categoryId) || 0;
		},
		[categoryNewsCountMap],
	);

	return {
		filteredNews,
		availableCategories,
		activeCategoryId,
		activeCategorySlug,
		handleCategoryChange,
		categorySlugMap,
		getNewsCountForCategory,
	};
}
